/*
 * Silverstore gift box
 * <ss-gift-box>    standalone page builder (sections/ss-gift-box.liquid)
 * <ss-gift-bundle> product page block (blocks/ss-gift-bundle.liquid)
 *
 * Shared config comes from the first script[data-ss-gift-config] on the page
 * (snippets/ss-gift-box-data.liquid). Prices shown here are previews: the
 * money comes off at checkout through Shopify automatic discounts that use the
 * same tier values. Everything goes to the cart with one /cart/add.js call,
 * grouped by a hidden _gift_box line property.
 */
(() => {
  if (window.customElements.get('ss-gift-box')) return;

  const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const esc = (value) =>
    String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const readJSON = (el) => {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      return null;
    }
  };

  /* ---------- config ---------- */
  let CFG = null;
  function config() {
    if (CFG) return CFG;
    CFG = readJSON(document.querySelector('script[data-ss-gift-config]')) || {};
    const seen = new Set();
    CFG.tiers = (CFG.tiers || [])
      .filter((t) => t.qty > 0 && t.pct > 0)
      .sort((a, b) => a.qty - b.qty)
      .filter((t) => (seen.has(t.qty) ? false : seen.add(t.qty)));
    const okBox = (b) => b && b.variants && b.variants.some((v) => v.available);
    const pk = CFG.packaging || {};
    const def = {};
    ['necklace', 'bracelet', 'ring', 'earrings'].forEach((k) => {
      if (okBox((pk.default || {})[k])) def[k] = pk.default[k];
    });
    const defIds = Object.values(def).map((b) => b.id);
    const ring = (pk.premiumRing || []).filter(okBox);
    const all = (pk.premiumAll || []).filter(okBox);
    if (!ring.length && !all.length) {
      (pk.premiumPool || [])
        .filter((b) => okBox(b) && !defIds.includes(b.id))
        .forEach((b) => {
          const fits = boxRules(b).fits;
          (fits.length === 1 && fits[0] === 'ring' ? ring : all).push(b);
        });
    }
    CFG.pack = { def, ring, all };
    CFG.strings = CFG.strings || {};
    return CFG;
  }
  document.addEventListener('shopify:section:load', () => {
    CFG = null;
  });

  function S(key, vars) {
    let text = config().strings[key] || '';
    if (vars) Object.keys(vars).forEach((k) => (text = text.split('[' + k + ']').join(vars[k])));
    return text;
  }

  /* ---------- money ---------- */
  function formatMoney(cents) {
    const format = config().moneyFormat || '{{amount}}';
    const value = Number(cents || 0) / 100;
    const num = (n, decimals, thousands, decimal) => {
      const [int, frac] = n.toFixed(decimals).split('.');
      const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? grouped + decimal + frac : grouped;
    };
    return format
      .replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
        switch (key) {
          case 'amount_no_decimals':
            return num(value, 0, ',', '.');
          case 'amount_with_comma_separator':
            return num(value, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator':
            return num(value, 0, '.', ',');
          case 'amount_with_apostrophe_separator':
            return num(value, 2, "'", '.');
          case 'amount_with_space_separator':
            return num(value, 2, ' ', ',');
          case 'amount_no_decimals_with_space_separator':
            return num(value, 0, ' ', ',');
          default:
            return num(value, 2, ',', '.');
        }
      })
      .replace(/<[^>]*>/g, '');
  }

  /* ---------- tiers ---------- */
  function tierInfo(count) {
    const tiers = config().tiers;
    let current = null;
    let next = null;
    tiers.forEach((t) => {
      if (count >= t.qty) current = t;
      else if (!next) next = t;
    });
    return { tiers, current, next, max: tiers.length ? tiers[tiers.length - 1].qty : 0 };
  }

  /* Only pieces in the discounted collections count toward the tiers. */
  function inOffer(product) {
    const ids = config().offer || [];
    if (!ids.length || !product) return true;
    return (product.colls || []).some((id) => ids.includes(id));
  }

  function renderChips(list) {
    if (!list) return;
    const tiers = config().tiers;
    list.innerHTML = tiers.map((t) => `<li>${esc(S('tierChip', { count: t.qty, percent: t.pct }))}</li>`).join('');
    list.hidden = !tiers.length;
  }

  /* ---------- piece kinds + packaging fit ---------- */
  const KINDS = [
    ['earrings', /earring|حلق|اقراط|أقراط/i],
    ['necklace', /necklace|chain|pendant|سلسل|سلاسل|قلاد|عقد/i],
    ['bracelet', /bracelet|bangle|anklet|أسور|اسور|انسيال|إنسيال|خلخال/i],
    ['ring', /ring|خاتم|خواتم|دبل/i],
  ];
  function normalizeKind(word) {
    const w = String(word || '').trim().toLowerCase();
    if (/earring/.test(w)) return 'earrings';
    if (/necklace/.test(w)) return 'necklace';
    if (/bracelet/.test(w)) return 'bracelet';
    if (/ring/.test(w)) return 'ring';
    return null;
  }
  function kindOf(product) {
    if (!product) return 'other';
    if (product.kind) return product.kind;
    const colls = product.colls || [];
    const map = config().kinds || {};
    for (const kind of ['earrings', 'necklace', 'bracelet', 'ring']) {
      if (map[kind] && colls.includes(map[kind])) return (product.kind = kind);
    }
    const text = `${product.type || ''} ${product.title || ''}`;
    for (const [kind, pattern] of KINDS) if (pattern.test(text)) return (product.kind = kind);
    return (product.kind = 'other');
  }
  const minPrice = (box) => {
    const prices = box.variants.filter((v) => v.available).map((v) => v.price);
    return prices.length ? Math.min(...prices) : Infinity;
  };
  const BOX_GUESS = [
    ['ring', /ring box|خاتم/i],
    ['necklace', /necklace box|سلسل/i],
    ['bracelet', /bracelet box|أسور|اسور/i],
    ['earrings', /earrings? box|حلق/i],
  ];
  function boxRules(box) {
    if (box.rules) return box.rules;
    let fits = (box.fits || []).filter(Boolean);
    let holds = box.holds || 0;
    if (!fits.length) {
      const guess = BOX_GUESS.find(([, pattern]) => pattern.test(box.title || ''));
      if (guess) {
        fits = [guess[0]];
        if (!holds) holds = 1;
      }
    }
    return (box.rules = { fits, holds });
  }
  const NAMED_COLORS = {
    red: '#b3202f', احمر: '#b3202f', أحمر: '#b3202f', pink: '#e8a3b5', وردي: '#e8a3b5', بينك: '#e8a3b5',
    rose: '#d98a9b', purple: '#6b3a7d', بنفسجي: '#6b3a7d', موف: '#8e6aa8', black: '#1f1f1f', اسود: '#1f1f1f',
    أسود: '#1f1f1f', white: '#ffffff', ابيض: '#ffffff', أبيض: '#ffffff', blue: '#2b4c8c', ازرق: '#2b4c8c',
    أزرق: '#2b4c8c', green: '#2a6b4c', اخضر: '#2a6b4c', أخضر: '#2a6b4c', gold: '#c9a96e', ذهبي: '#c9a96e',
    silver: '#c4c7cc', فضي: '#c4c7cc', burgundy: '#581623', نبيتي: '#581623', beige: '#e6d8c3', بيج: '#e6d8c3',
  };
  const swatchColor = (v) => v.color || NAMED_COLORS[String(v.colorName || '').trim().toLowerCase()] || '#d9d6d3';

  /* ---------- gift box drawing ---------- */
  const SLOTS = [
    [50, 30],
    [78, 24],
    [106, 30],
  ];
  function boxSVG(images) {
    const items = (images || [])
      .slice(0, 3)
      .map((src, i) => `<image href="${esc(src)}" x="${SLOTS[i][0]}" y="${SLOTS[i][1]}" width="44" height="44" preserveAspectRatio="xMidYMid slice"/>`)
      .join('');
    return (
      '<svg class="ssgb-boxsvg" viewBox="0 0 200 170" aria-hidden="true" focusable="false">' +
      '<rect class="ssgb-boxsvg__inside" x="30" y="62" width="140" height="12"/>' +
      `<g class="ssgb-boxsvg__items">${items}</g>` +
      '<rect class="ssgb-boxsvg__body" x="30" y="70" width="140" height="95"/>' +
      '<rect class="ssgb-boxsvg__shade" x="30" y="70" width="140" height="10"/>' +
      '<rect class="ssgb-boxsvg__ribbon" x="92" y="70" width="16" height="95"/>' +
      '<g class="ssgb-boxsvg__lid"><rect class="ssgb-boxsvg__lidtop" x="22" y="48" width="156" height="26"/>' +
      '<rect class="ssgb-boxsvg__ribbon" x="92" y="48" width="16" height="26"/>' +
      '<g class="ssgb-boxsvg__bow"><path d="M100 48C78 18 58 40 100 48Z"/><path d="M100 48C122 18 142 40 100 48Z"/><circle cx="100" cy="47" r="6"/></g></g>' +
      '</svg>'
    );
  }

  /* ---------- flight ---------- */
  function fly(fromEl, src, toEl, opts = {}) {
    if (REDUCE.matches || !fromEl || !toEl || (!src && !opts.node)) return Promise.resolve();
    const a = fromEl.getBoundingClientRect();
    const b = toEl.getBoundingClientRect();
    if (!a.width || !b.width) return Promise.resolve();
    let el;
    let w;
    let h;
    if (opts.node) {
      el = opts.node;
      w = a.width;
      h = a.height;
    } else {
      el = new Image();
      el.src = src;
      el.alt = '';
      el.className = 'ssgb-flyer';
      w = h = Math.min(a.width, a.height || a.width, 260);
    }
    el.setAttribute('aria-hidden', 'true');
    const x0 = a.left + a.width / 2;
    const y0 = a.top + a.height / 2;
    const x1 = b.left + b.width * (opts.tx ?? 0.5);
    const y1 = b.top + b.height * (opts.ty ?? 0.35);
    Object.assign(el.style, { position: 'fixed', width: `${w}px`, height: `${h}px`, left: `${x0 - w / 2}px`, top: `${y0 - h / 2}px` });
    document.body.appendChild(el);
    const end = Math.max((b.width * (opts.endScale ?? 0.3)) / w, 0.06);
    const lift = Math.min(240, 90 + Math.abs(x1 - x0) * 0.3);
    const spin = opts.spin ?? -14;
    const frames = [];
    for (let i = 0; i <= 16; i += 1) {
      const p = i / 16;
      const eased = p * p * (3 - 2 * p);
      frames.push({
        transform: `translate(${(x1 - x0) * p}px, ${(y1 - y0) * p - Math.sin(p * Math.PI) * lift}px) scale(${1 + (end - 1) * eased}) rotate(${spin * Math.sin(p * Math.PI)}deg)`,
        opacity: p > 0.9 ? Math.max(0, (1 - p) / 0.1) : 1,
      });
    }
    const animation = el.animate(frames, { duration: opts.duration || 820, easing: 'cubic-bezier(.4,0,.3,1)' });
    return animation.finished.catch(() => {}).then(() => el.remove());
  }

  function bump(el) {
    if (!el || REDUCE.matches) return;
    el.classList.remove('is-bump');
    void el.offsetWidth;
    el.classList.add('is-bump');
    setTimeout(() => el.classList.remove('is-bump'), 600);
  }

  function cartIcon() {
    const selectors = ['[data-ss-cart-icon]', '#cart-icon-bubble', '.header__minicart', 'a[href$="/cart"]'];
    for (const selector of selectors) {
      const found = [...document.querySelectorAll(selector)].find((el) => el.getBoundingClientRect().width > 0);
      if (found) return found;
    }
    return null;
  }

  /* ---------- cart ---------- */
  async function cartAdd(items) {
    const response = await fetch(config().cartAddUrl || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status) {
      const message = data && (data.description || data.message);
      throw new Error(typeof message === 'string' && message ? message : S('error'));
    }
    return data;
  }

  async function refreshCart(open = true) {
    document.dispatchEvent(new CustomEvent('ss:gift-box:added', { bubbles: true }));
    const api = window.essentialCartApi;
    if (api && typeof api.refreshCart === 'function') {
      try {
        await api.refreshCart();
      } catch (error) {
        /* drawer refresh is best effort */
      }
      if (open && typeof api.openCart === 'function') {
        try {
          api.openCart();
        } catch (error) {
          /* ignore */
        }
      }
      return true;
    }
    return false;
  }

  const newBoxId = () => `GB-${Date.now().toString(36).toUpperCase()}`;

  /* ---------- online payment discount ----------
     A checkbox that applies the prepaid discount code (Theme settings → Gift box)
     to the cart, the URL (?discount=CODE) and the discount cookie, so checkout
     picks it up. Unticking removes it. */
  let onlineState = false;
  let onlineLoad = null;
  const onlineCode = () => {
    const online = config().online;
    return online && online.code ? String(online.code) : '';
  };
  const cartBase = () => config().cartUrl || '/cart';
  const rootPath = () => (config().rootUrl || '/').replace(/\/?$/, '/');
  function loadOnline() {
    if (onlineLoad) return onlineLoad;
    const code = onlineCode().toLowerCase();
    const fromUrl = (new URLSearchParams(window.location.search).get('discount') || '').toLowerCase() === code;
    onlineLoad = fetch(`${cartBase()}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => fromUrl || (cart.discount_codes || []).some((d) => String(d.code).toLowerCase() === code))
      .catch(() => fromUrl)
      .then((on) => {
        onlineState = on;
        if (fromUrl) setOnline(true, true);
        return on;
      });
    return onlineLoad;
  }
  async function setOnline(on, quiet) {
    const code = onlineCode();
    if (!code) return;
    onlineState = on;
    document.querySelectorAll('[data-online-check]').forEach((box) => {
      box.checked = on;
    });
    try {
      await fetch(`${cartBase()}/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: on ? code : '' }),
      });
    } catch (error) {
      /* the cookie below still carries the code to checkout */
    }
    if (on) {
      try {
        await fetch(`${rootPath()}discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' });
      } catch (error) {
        /* ignore */
      }
    } else {
      document.cookie = 'discount_code=; Max-Age=0; path=/';
    }
    try {
      const url = new URL(window.location.href);
      if (on) url.searchParams.set('discount', code);
      else url.searchParams.delete('discount');
      window.history.replaceState(window.history.state, '', url);
    } catch (error) {
      /* ignore */
    }
    if (!quiet) refreshCart(false);
  }
  function mountOnline(el) {
    if (!el) return;
    const online = config().online;
    if (!online || !online.code) {
      el.innerHTML = '';
      return;
    }
    el.innerHTML =
      `<label class="ssgb-online"><input type="checkbox" data-online-check${onlineState ? ' checked' : ''}>` +
      `<span class="ssgb-online__text"><b>${esc(S('onlineLabel', { percent: online.pct }))}</b></span></label>`;
    el.addEventListener('change', (event) => {
      if (event.target.matches('[data-online-check]')) setOnline(event.target.checked);
    });
    loadOnline().then((on) => {
      const box = el.querySelector('[data-online-check]');
      if (box) box.checked = on;
    });
  }

  /* ---------- packaging picker (shared) ----------
     Standard boxes follow the pieces: one box per piece, matched by piece type,
     the shopper picks a colour per piece. Premium boxes: ring-only boxes (shown
     when the box has a ring) and boxes for any piece, pick as many as you like.
     Standard and premium can be combined. data-mode: both | default | premium. */
  const KIND_ORDER = ['necklace', 'bracelet', 'ring', 'earrings'];
  const liveVariants = (box) => box.variants.filter((v) => v.available);
  class Packaging {
    constructor(root, getPieces, onChange) {
      this.root = root;
      this.getPieces = getPieces;
      this.onChange = onChange || (() => {});
      this.mode = (root && root.dataset.mode) || 'both';
      this.on = false;
      this.tab = this.mode === 'premium' ? 'premium' : 'default';
      this.def = new Map();
      this.prem = new Map();
      this.scroll = {};
      if (!root) return;
      root.addEventListener('change', (event) => {
        const t = event.target;
        if (t.matches('[data-pack-toggle]')) {
          this.on = t.checked;
          this.render(true);
          this.onChange();
        } else if (t.matches('[data-pack-prem]')) {
          const id = Number(t.value);
          if (t.checked) {
            const box = this.findPremium(id);
            const variant = box && liveVariants(box)[0];
            if (variant) this.prem.set(id, variant.id);
          } else {
            this.prem.delete(id);
          }
          this.render();
          this.onChange();
          this.refocus(`[data-pack-prem][value="${id}"]`);
        }
      });
      root.addEventListener('click', (event) => {
        const tab = event.target.closest('[data-pack-tab]');
        if (tab) {
          this.tab = tab.dataset.packTab;
          this.render(true);
          this.refocus(`[data-pack-tab="${this.tab}"]`);
          return;
        }
        const chip = event.target.closest('[data-pack-def]');
        if (chip) {
          event.preventDefault();
          const key = chip.dataset.slot;
          const id = Number(chip.dataset.variant);
          if (this.def.get(key) === id) this.def.delete(key);
          else this.def.set(key, id);
          this.render();
          this.onChange();
          this.refocus(`[data-pack-def][data-slot="${key}"][data-variant="${id}"]`);
          return;
        }
        const swatch = event.target.closest('[data-pack-swatch]');
        if (swatch) {
          event.preventDefault();
          const id = Number(swatch.dataset.box);
          const variantId = Number(swatch.dataset.variant);
          this.prem.set(id, variantId);
          this.render();
          this.onChange();
          this.refocus(`[data-pack-swatch][data-box="${id}"][data-variant="${variantId}"]`);
        }
      });
      this.render();
    }

    refocus(selector) {
      const el = this.root.querySelector(selector);
      if (el) el.focus({ preventScroll: true });
    }

    showDefault() {
      return this.mode === 'both' || this.mode === 'default';
    }

    showPremium() {
      return this.mode === 'both' || this.mode === 'premium';
    }

    pieces() {
      return (this.getPieces() || []).filter((p) => p && p.product && p.variant);
    }

    slots() {
      const def = config().pack.def;
      const out = [];
      this.pieces().forEach((p) => {
        const kind = kindOf(p.product);
        const box = def[kind];
        if (!box) return;
        for (let i = 0; i < p.qty; i += 1) out.push({ key: `${p.variant.id}-${i}`, piece: p, kind, box });
      });
      return out.sort((a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind));
    }

    premiumLists() {
      const { ring, all } = config().pack;
      const hasRing = this.pieces().some((p) => kindOf(p.product) === 'ring');
      return { ring: hasRing ? ring : [], all };
    }

    findPremium(id) {
      const { ring, all } = config().pack;
      return ring.concat(all).find((b) => b.id === id);
    }

    prune() {
      const keys = new Set(this.showDefault() ? this.slots().map((s) => s.key) : []);
      [...this.def.keys()].forEach((k) => {
        if (!keys.has(k)) this.def.delete(k);
      });
      const { ring, all } = this.premiumLists();
      const ids = new Set(this.showPremium() ? ring.concat(all).map((b) => b.id) : []);
      [...this.prem.keys()].forEach((k) => {
        if (!ids.has(k)) this.prem.delete(k);
      });
    }

    selectedAll() {
      if (!this.on) return [];
      this.prune();
      const out = [];
      const slots = this.slots();
      this.def.forEach((variantId, key) => {
        const slot = slots.find((s) => s.key === key);
        const variant = slot && slot.box.variants.find((v) => v.id === variantId && v.available);
        if (variant) out.push({ box: slot.box, variant, type: 'default' });
      });
      this.prem.forEach((variantId, id) => {
        const box = this.findPremium(id);
        const variant = box && box.variants.find((v) => v.id === variantId && v.available);
        if (variant) out.push({ box, variant, type: 'premium' });
      });
      return out;
    }

    total() {
      return this.selectedAll().reduce((n, s) => n + s.variant.price, 0);
    }

    cartItems(boxId) {
      const merged = new Map();
      this.selectedAll().forEach((s) => {
        const key = `${s.variant.id}:${s.type}`;
        const line = merged.get(key) || { id: s.variant.id, quantity: 0, properties: { _gift_box: boxId, _gift_box_packaging: s.type } };
        line.quantity += 1;
        merged.set(key, line);
      });
      return [...merged.values()];
    }

    reset() {
      this.on = false;
      this.def.clear();
      this.prem.clear();
      this.render();
    }

    defaultPanel() {
      if (!this.pieces().length) return `<p class="ssgb-pack__note">${esc(S('packEmpty'))}</p>`;
      const slots = this.slots();
      if (!slots.length) return `<p class="ssgb-pack__note">${esc(S('noStandard'))}</p>`;
      const groups = [];
      slots.forEach((s) => {
        let group = groups.find((g) => g.kind === s.kind);
        if (!group) groups.push((group = { kind: s.kind, box: s.box, slots: [] }));
        group.slots.push(s);
      });
      const html = groups
        .map((g) => {
          const variants = liveVariants(g.box);
          const head =
            `<p class="ssgb-pack__group"><span>${esc(S(`kind_${g.kind}`))}</span>` +
            `<span class="ssgb-pack__meta">${esc(S('perBox', { price: formatMoney(minPrice(g.box)) }))}</span></p>`;
          const rows = g.slots
            .map((s) => {
              const chosen = this.def.get(s.key);
              const chosenVariant = chosen ? g.box.variants.find((v) => v.id === chosen) : null;
              const boxImage = (chosenVariant && chosenVariant.image) || g.box.image;
              const pieceImage = s.piece.variant.image || s.piece.product.image;
              const chips = variants
                .map((v) => {
                  const label = v.colorName || (variants.length === 1 ? S('addThisBox') : v.title);
                  const dot = v.colorName ? `<i style="background:${esc(swatchColor(v))}"></i>` : '';
                  return `<button type="button" class="ssgb-pack__chip" data-pack-def data-slot="${s.key}" data-variant="${v.id}" aria-pressed="${chosen === v.id}">${dot}<span>${esc(label)}</span></button>`;
                })
                .join('');
              return (
                `<div class="ssgb-pack__slot${chosenVariant ? ' is-on' : ''}">` +
                `<span class="ssgb-pack__thumb">${pieceImage ? `<img src="${esc(pieceImage)}" alt="" width="48" height="48" loading="lazy">` : ''}</span>` +
                `<span class="ssgb-pack__slot-main"><span class="ssgb-pack__slot-name">${esc(s.piece.product.title)}</span>` +
                `<span class="ssgb-pack__chips" role="group" aria-label="${esc(S('colorFor', { title: s.piece.product.title }))}">${chips}</span></span>` +
                `<span class="ssgb-pack__thumb ssgb-pack__thumb--box">${boxImage ? `<img src="${esc(boxImage)}" alt="" width="48" height="48" loading="lazy">` : ''}</span>` +
                '</div>'
              );
            })
            .join('');
          return `<div class="ssgb-pack__sec">${head}${rows}</div>`;
        })
        .join('');
      return html;
    }

    card(box) {
      const chosen = this.prem.get(box.id);
      const isSelected = Boolean(chosen);
      const variant = (isSelected && box.variants.find((v) => v.id === chosen)) || liveVariants(box)[0] || box.variants[0];
      const image = (variant && variant.image) || box.image;
      const colored = box.variants.filter((v) => v.colorName);
      const swatches =
        colored.length > 1
          ? `<span class="ssgb-pack__swatches">${colored
              .map(
                (v) =>
                  `<button type="button" class="ssgb-pack__swatch" data-pack-swatch data-box="${box.id}" data-variant="${v.id}" style="background:${esc(swatchColor(v))}" aria-label="${esc(S('colorLabel', { color: v.colorName }))}" aria-pressed="${isSelected && v.id === variant.id}"${v.available ? '' : ' disabled'}></button>`
              )
              .join('')}</span>`
          : '';
      const holds = boxRules(box).holds;
      const meta = holds > 1 ? `<span class="ssgb-pack__meta">${esc(S('holds', { count: holds }))}</span>` : '';
      return (
        `<label class="ssgb-pack__card"><input type="checkbox" value="${box.id}" data-pack-prem${isSelected ? ' checked' : ''}>` +
        '<span class="ssgb-pack__ok" aria-hidden="true"></span>' +
        `<span class="ssgb-pack__img">${image ? `<img src="${esc(image)}" alt="" loading="lazy" width="240" height="240">` : ''}</span>` +
        `<span class="ssgb-pack__name">${esc(box.title)}</span>${meta}${swatches}` +
        `<span class="ssgb-pack__price">${esc(formatMoney(variant ? variant.price : minPrice(box)))}</span></label>`
      );
    }

    premiumPanel() {
      const { ring, all } = this.premiumLists();
      const row = (id, title, list) =>
        `<div class="ssgb-pack__sec"><p class="ssgb-pack__group"><span>${esc(title)}</span></p>` +
        `<div class="ssgb-pack__row" data-row="${id}">${list.map((b) => this.card(b)).join('')}</div></div>`;
      let html = '';
      if (ring.length) html += row('ring', S('forRing'), ring);
      if (all.length) html += row('all', ring.length ? S('premiumMore') : S('premiumAll'), all);
      if (!html) {
        const none = !config().pack.ring.length && !config().pack.all.length;
        return `<p class="ssgb-pack__note">${esc(S(none ? 'noPremium' : 'packEmpty'))}</p>`;
      }
      return html;
    }

    render(opening) {
      if (!this.root) return;
      const pack = config().pack;
      const boxes = [];
      if (this.showDefault()) boxes.push(...Object.values(pack.def));
      if (this.showPremium()) boxes.push(...pack.ring, ...pack.all);
      if (this.mode === 'off') {
        this.root.innerHTML = '';
        return;
      }
      const picked = this.selectedAll();
      const from = boxes.length ? Math.min(...boxes.map(minPrice)) : Infinity;
      const side = picked.length
        ? `${picked.length === 1 ? S('boxOne') : S('boxOther', { count: picked.length })} · ${formatMoney(this.total())}`
        : Number.isFinite(from)
          ? S('packagingFrom', { price: formatMoney(from) })
          : '';
      const head =
        `<label class="ssgb-pack__toggle"><input type="checkbox" data-pack-toggle${this.on ? ' checked' : ''}>` +
        `<span>${esc(S('packaging'))}</span>${side ? `<span class="ssgb-pack__from">${esc(side)}</span>` : ''}</label>`;
      let drawer = '';
      if (this.on) {
        const both = this.showDefault() && this.showPremium();
        if (!both) this.tab = this.showDefault() ? 'default' : 'premium';
        const countOf = (type) => picked.filter((s) => s.type === type).length;
        const tabs = both
          ? `<div class="ssgb-pack__tabs">${['default', 'premium']
              .map(
                (t) =>
                  `<button type="button" class="ssgb-pack__tab" data-pack-tab="${t}" aria-pressed="${this.tab === t}">` +
                  `<span>${esc(S(t === 'default' ? 'tabDefault' : 'tabPremium'))}</span>` +
                  (countOf(t) ? `<span class="ssgb-pack__count">${countOf(t)}</span>` : '') +
                  '</button>'
              )
              .join('')}</div>`
          : '';
        const body = this.tab === 'premium' ? this.premiumPanel() : this.defaultPanel();
        drawer = `<div class="ssgb-pack__drawer${opening ? ' is-opening' : ''}">${tabs}${body}</div>`;
      }
      this.root.querySelectorAll('[data-row]').forEach((row) => {
        this.scroll[row.dataset.row] = row.scrollLeft;
      });
      const toggleFocused = this.root.contains(document.activeElement) && document.activeElement.matches('[data-pack-toggle]');
      this.root.innerHTML = head + drawer;
      if (!opening) {
        this.root.querySelectorAll('[data-row]').forEach((row) => {
          row.scrollLeft = this.scroll[row.dataset.row] || 0;
        });
      }
      if (toggleFocused) this.root.querySelector('[data-pack-toggle]').focus();
    }
  }

  /* ---------- standalone page builder ---------- */
  class SSGiftBox extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.min = Math.max(1, Number(this.dataset.min || 1));
      this.max = Number(this.dataset.max || 0);
      this.items = [];
      this.products = new Map();
      this.cards = new Map();
      this.querySelectorAll('[data-card]').forEach((li) => {
        const product = readJSON(li.querySelector('[data-card-json]'));
        if (!product) return;
        if (!inOffer(product)) {
          li.remove();
          return;
        }
        this.products.set(product.id, product);
        this.cards.set(product.id, li);
        const actions = li.querySelector('[data-card-actions]');
        if (actions) li.addHTML = actions.innerHTML;
      });
      this.querySelectorAll('[data-ready-json]').forEach((el) => {
        const product = readJSON(el);
        if (product && !this.products.has(product.id)) this.products.set(product.id, product);
      });

      const q = (selector) => this.querySelector(selector);
      this.el = {
        box: q('[data-box]'),
        bandFill: q('[data-band-fill]'),
        ticks: q('[data-ticks]'),
        s1: q('[data-save1]'),
        s2: q('[data-save2]'),
        s3: q('[data-save3]'),
        items: q('[data-items]'),
        count: q('[data-count-label]'),
        complete: q('[data-complete]'),
        clear: q('[data-clear]'),
        gift: q('[data-gift]'),
        old: q('[data-old]'),
        total: q('[data-total]'),
        error: q('[data-error]'),
        cta: q('[data-cta]'),
        ctaLabel: q('[data-cta-label]'),
        status: q('[data-status]'),
        bar: q('[data-bar]'),
        barFill: q('[data-bar-fill]'),
        barBox: q('[data-bar-box]'),
        bar1: q('[data-bar1]'),
        bar2: q('[data-bar2]'),
        openBtn: q('[data-open-sheet]'),
        closeBtn: q('[data-close-sheet]'),
        backdrop: q('[data-backdrop]'),
        dialog: q('[data-dialog]'),
      };
      renderChips(q('[data-tier-chips]'));
      this.pack = new Packaging(q('[data-pack]'), () => this.items, () => this.render());
      mountOnline(q('[data-online]'));

      this.grid = q('[data-grid]');
      this.suggestEl = q('[data-suggest]');
      const kinds = String(this.dataset.kindOrder || '')
        .split(',')
        .map(normalizeKind)
        .filter(Boolean);
      this.kindOrder = [...new Set(kinds.length ? kinds : ['necklace', 'bracelet', 'ring', 'earrings'])];
      ['necklace', 'bracelet', 'ring', 'earrings'].forEach((k) => {
        if (!this.kindOrder.includes(k)) this.kindOrder.push(k);
      });
      this.cards.forEach((li, id) => {
        li.stock = Number(li.dataset.stock || 0);
        li.kind = kindOf(this.products.get(id));
      });
      this.arrange(false);

      this.onClick = this.onClick.bind(this);
      this.onKey = (event) => {
        if (event.key === 'Escape' && this.classList.contains('is-sheet-open')) this.toggleSheet(false);
      };
      this.addEventListener('click', this.onClick);
      document.addEventListener('keydown', this.onKey);
      this.lastTier = null;
      this.render();
      this.prefill();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKey);
      document.documentElement.style.removeProperty('overflow');
    }

    count() {
      return this.items.reduce((n, i) => n + i.qty, 0);
    }

    offerCount() {
      return this.items.reduce((n, i) => n + (inOffer(i.product) ? i.qty : 0), 0);
    }

    usingBar() {
      return this.el.bar && getComputedStyle(this.el.bar).display !== 'none' && !this.classList.contains('is-sheet-open');
    }

    flyTarget() {
      return this.usingBar() ? this.el.barBox : this.el.box;
    }

    onClick(event) {
      if (event.target === this.el.dialog) {
        this.el.dialog.close();
        return;
      }
      const t = event.target.closest('button, [data-backdrop]');
      if (!t || !this.contains(t)) return;
      const card = t.closest('[data-card]');
      const product = card ? this.products.get(Number(card.dataset.productId)) : null;

      if (t.matches('[data-add]') && product) {
        if (t.hasAttribute('data-multi')) this.openDialog(product, card);
        else this.add(product, product.variants.find((v) => v.available), card);
      } else if (t.matches('[data-inc]') && product) {
        if (t.dataset.variant) this.add(product, product.variants.find((v) => v.id === Number(t.dataset.variant)), card);
        else this.openDialog(product, card);
      } else if (t.matches('[data-dec]')) {
        this.removeOne(Number(t.dataset.variant));
      } else if (t.matches('[data-remove]')) {
        this.items = this.items.filter((i) => i.variant.id !== Number(t.dataset.variant));
        this.render();
        this.arrange(true);
      } else if (t.matches('[data-ready]')) {
        this.fillReady(t.dataset.ready);
      } else if (t.matches('[data-complete]')) {
        this.complete();
      } else if (t.matches('[data-clear]')) {
        this.items = [];
        this.render();
        this.arrange(true);
      } else if (t.matches('[data-cta]')) {
        this.submit();
      } else if (t.matches('[data-open-sheet]')) {
        this.toggleSheet(true);
      } else if (t.matches('[data-close-sheet]') || t.matches('[data-backdrop]')) {
        this.toggleSheet(false);
      } else if (t.matches('[data-dialog-close]')) {
        this.el.dialog.close();
      } else if (t.matches('[data-dialog-option]')) {
        this.dialogVariant = Number(t.dataset.variant);
        this.updateDialog();
      } else if (t.matches('[data-dialog-add]')) {
        const p = this.dialogProduct;
        const v = p && p.variants.find((x) => x.id === this.dialogVariant);
        if (p && v) {
          this.el.dialog.close();
          this.add(p, v, this.dialogCard);
        }
      }
    }

    add(product, variant, card, silent) {
      if (!variant || !variant.available || !inOffer(product)) return false;
      if (this.max && this.count() >= this.max) {
        this.say(S('maxPieces', { count: this.max }));
        return false;
      }
      const existing = this.items.find((i) => i.variant.id === variant.id);
      if (existing) existing.qty += 1;
      else this.items.push({ product, variant, qty: 1 });
      if (silent) return true;

      const media = card ? card.querySelector('[data-card-media]') : null;
      const target = this.flyTarget();
      this.render(true);
      fly(media, variant.image || product.image, target).then(() => {
        this.renderBox();
        bump(target);
        if (this.arrange(true)) this.revealGrid();
      });
      return true;
    }

    /* Grid order: highest stock first. Once the box has pieces, the types not
       yet in the box come first, in the editor's type order (after a necklace:
       bracelets, rings, earrings), each type still sorted by stock. */
    arrange(animate) {
      if (!this.grid) return false;
      const cards = [...this.grid.children].filter((el) => el.matches('[data-card]'));
      cards.forEach((li, i) => {
        if (li.baseIndex === undefined) li.baseIndex = i;
      });
      const inBox = new Set(this.items.map((i) => kindOf(i.product)));
      let ranks = null;
      let next = [];
      if (this.items.length) {
        next = this.kindOrder.filter((k) => !inBox.has(k));
        const done = this.kindOrder.filter((k) => inBox.has(k));
        ranks = new Map(next.concat(done).map((k, i) => [k, i]));
      }
      const rank = (li) => (ranks && ranks.has(li.kind) ? ranks.get(li.kind) : ranks ? 99 : 0);
      const sorted = cards.slice().sort((a, b) => rank(a) - rank(b) || b.stock - a.stock || a.baseIndex - b.baseIndex);

      if (this.suggestEl) {
        const names = next.slice(0, 3).map((k) => S(`kind_${k}`)).filter(Boolean);
        this.suggestEl.hidden = !names.length;
        this.suggestEl.textContent = names.length ? S('suggest', { list: names.join(' · ') }) : '';
      }

      const changed = sorted.some((li, i) => li !== cards[i]);
      if (!changed) {
        cards.forEach((li) => li.style.removeProperty('order'));
        return false;
      }
      const before = animate && !REDUCE.matches ? new Map(cards.map((li) => [li, li.getBoundingClientRect()])) : null;
      const fragment = document.createDocumentFragment();
      sorted.forEach((li) => {
        li.style.removeProperty('order');
        fragment.appendChild(li);
      });
      this.grid.appendChild(fragment);
      if (before) {
        const vh = window.innerHeight;
        sorted.forEach((li) => {
          const a = before.get(li);
          const b = li.getBoundingClientRect();
          const dx = a.left - b.left;
          const dy = a.top - b.top;
          const visible = (b.bottom > 0 && b.top < vh) || (a.bottom > 0 && a.top < vh);
          if ((dx || dy) && visible) {
            li.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }], { duration: 480, easing: 'cubic-bezier(.22,.8,.2,1)' });
          }
        });
      }
      return true;
    }

    revealGrid() {
      const anchor = this.suggestEl && !this.suggestEl.hidden ? this.suggestEl : this.grid;
      if (!anchor) return;
      const offset = parseFloat(getComputedStyle(this).getPropertyValue('--ssgb-sticky-top')) || 100;
      const top = anchor.getBoundingClientRect().top;
      if (top < offset - 8 || top > window.innerHeight * 0.6) {
        window.scrollTo({ top: window.scrollY + top - offset, behavior: REDUCE.matches ? 'auto' : 'smooth' });
      }
    }

    removeOne(variantId) {
      const item = this.items.find((i) => i.variant.id === variantId);
      if (!item) return;
      item.qty -= 1;
      if (item.qty <= 0) this.items = this.items.filter((i) => i !== item);
      this.render();
      this.arrange(true);
    }

    fillReady(csv) {
      const ids = String(csv || '')
        .split(',')
        .map(Number)
        .filter(Boolean);
      this.items = [];
      ids.forEach((id) => {
        for (const product of this.products.values()) {
          const variant = product.variants.find((v) => v.id === id);
          if (variant) {
            this.add(product, variant, null, true);
            break;
          }
        }
      });
      this.render();
      bump(this.flyTarget());
      if (this.arrange(true)) this.revealGrid();
    }

    complete() {
      const info = tierInfo(this.offerCount());
      if (!info.next) return;
      let need = info.next.qty - this.offerCount();
      const inBox = new Set(this.items.map((i) => i.product.id));
      const queues = new Map();
      [...this.cards.values()]
        .sort((a, b) => b.stock - a.stock || a.baseIndex - b.baseIndex)
        .forEach((li) => {
          const id = Number(li.dataset.productId);
          if (inBox.has(id) || !inOffer(this.products.get(id))) return;
          if (!queues.has(li.kind)) queues.set(li.kind, []);
          queues.get(li.kind).push(this.products.get(id));
        });
      const kindsInBox = new Set(this.items.map((i) => kindOf(i.product)));
      const turn = this.kindOrder.filter((k) => !kindsInBox.has(k)).concat(this.kindOrder.filter((k) => kindsInBox.has(k)), ['other']);
      let guard = 0;
      while (need > 0 && guard < 200) {
        guard += 1;
        let added = false;
        for (const kind of turn) {
          if (need <= 0) break;
          const queue = queues.get(kind) || [];
          while (queue.length) {
            const product = queue.shift();
            const variant = product && product.variants.find((v) => v.available);
            if (variant && this.add(product, variant, null, true)) {
              need -= 1;
              added = true;
              break;
            }
          }
        }
        if (!added) break;
      }
      this.render();
      bump(this.flyTarget());
      if (this.arrange(true)) this.revealGrid();
    }

    async prefill() {
      const params = new URLSearchParams(window.location.search);
      const handle = params.get('box_add');
      const variantId = Number(params.get('box_variant'));
      if (!handle) return;
      let product = [...this.products.values()].find((p) => p.handle === handle);
      if (!product) {
        try {
          const root = (config().rootUrl || '/').replace(/\/?$/, '/');
          const raw = await fetch(`${root}products/${encodeURIComponent(handle)}.js`).then((r) => (r.ok ? r.json() : null));
          if (raw) {
            product = {
              id: raw.id,
              handle: raw.handle,
              title: raw.title,
              type: raw.type,
              url: raw.url,
              image: raw.featured_image || null,
              variants: raw.variants.map((v) => ({ id: v.id, title: v.title, price: v.price, available: v.available, image: v.featured_image ? v.featured_image.src : null })),
            };
            this.products.set(product.id, product);
          }
        } catch (error) {
          product = null;
        }
      }
      if (product) {
        const variant = product.variants.find((v) => v.id === variantId && v.available) || product.variants.find((v) => v.available);
        if (variant) this.add(product, variant, null, true);
        this.render();
        this.arrange(false);
      }
      params.delete('box_add');
      params.delete('box_variant');
      const query = params.toString();
      window.history.replaceState(window.history.state, '', window.location.pathname + (query ? `?${query}` : '') + window.location.hash);
    }

    openDialog(product, card) {
      if (!this.el.dialog) return;
      this.dialogProduct = product;
      this.dialogCard = card;
      const first = product.variants.find((v) => v.available);
      this.dialogVariant = first ? first.id : null;
      this.updateDialog();
      if (typeof this.el.dialog.showModal === 'function') this.el.dialog.showModal();
      else this.el.dialog.setAttribute('open', '');
    }

    updateDialog() {
      const p = this.dialogProduct;
      if (!p) return;
      const d = this.el.dialog;
      const variant = p.variants.find((v) => v.id === this.dialogVariant);
      d.querySelector('[data-dialog-title]').textContent = p.title;
      d.querySelector('[data-dialog-price]').textContent = variant ? formatMoney(variant.price) : '';
      const img = d.querySelector('[data-dialog-img]');
      const src = (variant && variant.image) || p.image;
      if (src) img.src = src;
      img.alt = p.title;
      d.querySelector('[data-dialog-options]').innerHTML = p.variants
        .map(
          (v) =>
            `<button type="button" class="ssgb-chip" role="radio" data-dialog-option data-variant="${v.id}" aria-checked="${v.id === this.dialogVariant}"${v.available ? '' : ' disabled'}>${esc(v.title)}</button>`
        )
        .join('');
      const addBtn = d.querySelector('[data-dialog-add]');
      addBtn.disabled = !variant || !variant.available;
    }

    toggleSheet(open) {
      this.classList.toggle('is-sheet-open', open);
      if (this.el.backdrop) this.el.backdrop.hidden = !open;
      if (this.el.openBtn) this.el.openBtn.setAttribute('aria-expanded', String(open));
      if (open) {
        document.documentElement.style.overflow = 'hidden';
        if (this.el.closeBtn) this.el.closeBtn.focus({ preventScroll: true });
      } else {
        document.documentElement.style.removeProperty('overflow');
        if (this.el.openBtn && this.contains(document.activeElement)) this.el.openBtn.focus({ preventScroll: true });
      }
    }

    say(message) {
      if (this.el.status) this.el.status.textContent = message;
      if (this.el.error) {
        this.el.error.textContent = message;
        this.el.error.hidden = false;
        clearTimeout(this.sayTimer);
        this.sayTimer = setTimeout(() => {
          this.el.error.hidden = true;
        }, 3000);
      }
    }

    renderBox() {
      const images = [];
      this.items.forEach((i) => {
        for (let n = 0; n < i.qty; n += 1) images.push(i.variant.image || i.product.image);
      });
      const svg = boxSVG(images.filter(Boolean));
      if (this.el.box) this.el.box.innerHTML = svg;
      if (this.el.barBox) this.el.barBox.innerHTML = svg;
    }

    renderCards(count) {
      const atMax = this.max && count >= this.max;
      this.cards.forEach((li, id) => {
        const product = this.products.get(id);
        const mine = this.items.filter((i) => i.product.id === id);
        const qty = mine.reduce((n, i) => n + i.qty, 0);
        const actions = li.querySelector('[data-card-actions]');
        li.classList.toggle('is-in', qty > 0);
        const last = mine[mine.length - 1];
        const state = qty > 0 ? `q${qty}-${last.variant.id}-${atMax ? 1 : 0}` : 'add';
        if (!actions || actions.dataset.state === state) return;
        actions.dataset.state = state;
        if (qty > 0) {
          const multi = product.variants.length > 1;
          actions.innerHTML =
            '<div class="ssgb-step">' +
            `<button type="button" class="ssgb-step__btn" data-dec data-variant="${last.variant.id}" aria-label="${esc(S('decrease', { title: product.title }))}">−</button>` +
            `<span class="ssgb-step__label">${esc(`${qty} · ${S('inBox')}`)}</span>` +
            `<button type="button" class="ssgb-step__btn" data-inc${multi ? '' : ` data-variant="${last.variant.id}"`} aria-label="${esc(S('increase', { title: product.title }))}"${atMax ? ' disabled' : ''}>+</button>` +
            '</div>';
        } else {
          actions.innerHTML = li.addHTML || '';
        }
      });
    }

    render(skipBox) {
      const cfg = config();
      const count = this.count();
      const tcount = this.offerCount();
      const info = tierInfo(tcount);
      this.renderCards(count);
      if (!skipBox) this.renderBox();

      /* savings bar + ticks */
      const max = Math.max(info.max, 1);
      const fill = `${Math.min(tcount / max, 1) * 100}%`;
      if (this.el.bandFill) this.el.bandFill.style.width = fill;
      if (this.el.barFill) this.el.barFill.style.width = fill;
      if (this.el.ticks) {
        this.el.ticks.innerHTML = info.tiers
          .map((t) => {
            const on = tcount >= t.qty;
            const pop = on && this.lastTier !== info.current && info.current && info.current.qty === t.qty;
            return `<span class="ssgb__tick${on ? ' is-on' : ''}${pop ? ' is-pop' : ''}${t.qty === max ? ' is-end' : ''}" style="inset-inline-start:${(t.qty / max) * 100}%"><i></i><span>${t.qty} · ${t.pct}%</span></span>`;
          })
          .join('');
        if (this.el.ticks.querySelector('.is-pop')) setTimeout(() => this.el.ticks.querySelectorAll('.is-pop').forEach((el) => el.classList.remove('is-pop')), 350);
      }

      /* savings text */
      const pieces = this.items.reduce((n, i) => n + i.variant.price * i.qty, 0);
      const pct = info.current ? info.current.pct : 0;
      const offerPieces = this.items.reduce((n, i) => n + (inOffer(i.product) ? i.variant.price * i.qty : 0), 0);
      const save = Math.round((offerPieces * pct) / 100);
      const piecesLabel = count === 1 ? S('piecesOne') : S('piecesOther', { count });
      let l1 = '';
      let l2 = '';
      let l3 = '';
      if (!count) {
        l1 = S('empty');
        if (info.tiers[0]) l2 = S('tierChip', { count: info.tiers[0].qty, percent: info.tiers[0].pct });
      } else if (!info.current) {
        l1 = piecesLabel;
        if (info.next) l2 = S('addToUnlock', { count: info.next.qty - tcount, percent: info.next.pct });
        l3 = esc(formatMoney(pieces));
      } else {
        l1 = info.next ? S('unlockedNext', { percent: pct, count: info.next.qty - tcount, next: info.next.pct }) : S('unlocked', { percent: pct });
        l2 = S('youSave', { amount: formatMoney(save) });
        l3 = `<s>${esc(formatMoney(pieces))}</s>${esc(formatMoney(pieces - save))}`;
      }
      if (this.el.s1) this.el.s1.textContent = l1;
      if (this.el.s2) {
        this.el.s2.textContent = l2;
        if (info.current && this.lastTier !== info.current) {
          this.el.s2.classList.remove('is-pop');
          void this.el.s2.offsetWidth;
          this.el.s2.classList.add('is-pop');
        }
      }
      if (this.el.s3) this.el.s3.innerHTML = l3;
      const countLabel = info.max && count <= info.max ? S('countOf', { count, max: info.max }) : piecesLabel;
      if (this.el.count) this.el.count.textContent = countLabel;
      if (this.lastCount !== undefined && this.lastCount !== count && this.el.status) this.el.status.textContent = `${countLabel}. ${l2}`;
      this.lastCount = count;
      this.lastTier = info.current;

      /* chosen pieces */
      if (this.el.items) {
        this.el.items.innerHTML = this.items
          .map(
            (i) =>
              `<li class="ssgb__item"><img src="${esc(i.variant.image || i.product.image || '')}" alt="${esc(i.product.title)}" width="56" height="56" loading="lazy">` +
              (i.qty > 1 ? `<span class="ssgb__item-qty">${i.qty}</span>` : '') +
              `<button type="button" class="ssgb__item-x" data-remove data-variant="${i.variant.id}" aria-label="${esc(S('remove', { title: i.product.title }))}">×</button></li>`
          )
          .join('');
      }
      if (this.el.complete) this.el.complete.hidden = !(count > 0 && info.next);
      if (this.el.clear) this.el.clear.hidden = count === 0;

      /* free gift */
      const giftQty = cfg.freeGift ? Number(cfg.freeGiftQty || 0) : 0;
      if (this.el.gift) {
        this.el.gift.hidden = !giftQty;
        if (giftQty) {
          const unlocked = tcount >= giftQty;
          this.el.gift.classList.toggle('is-on', unlocked);
          this.el.gift.innerHTML =
            (cfg.freeGift.image ? `<img src="${esc(cfg.freeGift.image)}" alt="" width="44" height="44" loading="lazy">` : '') +
            `<span>${esc(unlocked ? S('giftUnlocked') : S('giftLocked', { count: giftQty }))}</span>`;
        }
      }

      /* packaging + totals */
      this.pack.render();
      const packPrice = this.pack.total();
      const total = pieces - save + packPrice;
      if (this.el.old) this.el.old.textContent = save > 0 ? formatMoney(pieces + packPrice) : '';
      if (this.el.total) this.el.total.textContent = formatMoney(total);
      if (this.el.cta && !this.busy) {
        const tooFew = count < this.min;
        this.el.cta.disabled = tooFew;
        if (!count) this.el.ctaLabel.textContent = S('addBox');
        else this.el.ctaLabel.textContent = tooFew ? S('minPieces', { count: this.min }) : `${S('addBox')} · ${formatMoney(total)}`;
      }

      /* mobile bar */
      if (this.el.bar1) {
        this.el.bar1.textContent = !count ? S('empty') : info.current ? S('youSave', { amount: formatMoney(save) }) : piecesLabel;
      }
      if (this.el.bar2) {
        let line = '';
        if (!count) {
          if (info.tiers[0]) line = S('tierChip', { count: info.tiers[0].qty, percent: info.tiers[0].pct });
        } else if (info.next) {
          line = S('addMore', { count: info.next.qty - tcount, percent: info.next.pct });
        } else {
          line = formatMoney(total);
        }
        this.el.bar2.textContent = line;
      }
    }

    async submit() {
      if (this.busy) return;
      const count = this.count();
      if (count < this.min) return;
      const cfg = config();
      this.busy = true;
      this.el.error.hidden = true;
      this.el.cta.disabled = true;
      this.el.cta.setAttribute('aria-busy', 'true');
      this.el.ctaLabel.textContent = S('adding');

      let added = false;
      const boxId = newBoxId();
      const items = this.items.map((i) => ({ id: i.variant.id, quantity: i.qty, properties: { _gift_box: boxId } }));
      items.push(...this.pack.cartItems(boxId));
      if (cfg.freeGift && cfg.freeGiftQty && count >= cfg.freeGiftQty) {
        const giftVariant = cfg.freeGift.variants.find((v) => v.available);
        if (giftVariant) items.push({ id: giftVariant.id, quantity: 1, properties: { _gift_box: boxId, _free_gift: '1' } });
      }

      try {
        await cartAdd(items);
        await this.closeAndSend();
        this.items = [];
        this.pack.reset();
        this.toggleSheet(false);
        added = true;
        const handled = await refreshCart(true);
        if (!handled) {
          window.location.href = cfg.cartUrl || '/cart';
          return;
        }
      } catch (error) {
        this.el.error.textContent = error.message || S('error');
        this.el.error.hidden = false;
      } finally {
        this.busy = false;
        this.el.cta.removeAttribute('aria-busy');
        this.render();
        if (added && this.el.status) this.el.status.textContent = S('added');
      }
    }

    async closeAndSend() {
      const boxEl = this.flyTarget();
      if (REDUCE.matches || !boxEl) return;
      boxEl.classList.add('is-closed');
      await wait(820);
      const cart = cartIcon();
      if (cart) {
        const node = boxEl.cloneNode(true);
        node.className = 'ssgb-flyer ssgb-flyer--box is-closed';
        await fly(boxEl, null, cart, { node, endScale: 0.9, spin: 0, duration: 700, ty: 0.5 });
      }
      boxEl.classList.remove('is-closed');
    }
  }

  /* ---------- product page block ---------- */
  class SSGiftBundle extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.main = readJSON(this.querySelector('[data-main-json]'));
      if (!this.main) return;
      this.rows = [...this.querySelectorAll('[data-rec]')]
        .map((row) => ({
          row,
          product: readJSON(row.querySelector('[data-rec-json]')),
          check: row.querySelector('[data-rec-check]'),
          select: row.querySelector('[data-rec-variant]'),
          price: row.querySelector('[data-rec-price]'),
          compare: row.querySelector('[data-rec-compare]'),
        }))
        .filter((r) => r.product && r.check);
      this.saveEl = this.querySelector('[data-save]');
      this.errorEl = this.querySelector('[data-error]');
      this.section = this.closest('.shopify-section') || document.body;
      renderChips(this.querySelector('[data-tier-chips]'));
      this.pack = new Packaging(this.querySelector('[data-pack]'), () => this.pieces(), () => this.render());
      mountOnline(this.querySelector('[data-online]'));

      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-rec-variant]')) {
          const r = this.rows.find((x) => x.select === event.target);
          if (r) r.check.value = event.target.value;
        }
        if (event.target.matches('[data-rec-check], [data-rec-variant]')) this.render();
      });
      this.onSubmit = this.onSubmit.bind(this);
      this.onBuyNow = this.onBuyNow.bind(this);
      this.onFormChange = (event) => {
        if (this.contains(event.target)) return;
        setTimeout(() => this.render(), 60);
        setTimeout(() => this.render(), 450);
      };
      document.addEventListener('submit', this.onSubmit, true);
      document.addEventListener('click', this.onBuyNow, true);
      this.section.addEventListener('change', this.onFormChange);
      this.setupRail();
      this.render();
    }

    setupRail() {
      const track = this.querySelector('[data-track]');
      if (!track) return;
      const prev = this.querySelector('[data-prev]');
      const next = this.querySelector('[data-next]');
      const sync = () => {
        const x = Math.abs(track.scrollLeft);
        if (prev) prev.hidden = x < 4;
        if (next) next.hidden = x + track.clientWidth >= track.scrollWidth - 4;
      };
      const go = (sign) => {
        const card = track.querySelector('[data-rec]');
        const step = card ? card.getBoundingClientRect().width + 10 : track.clientWidth * 0.8;
        const rtl = getComputedStyle(track).direction === 'rtl';
        track.scrollBy({ left: sign * step * (rtl ? -1 : 1), behavior: 'smooth' });
      };
      if (prev) prev.addEventListener('click', () => go(-1));
      if (next) next.addEventListener('click', () => go(1));
      track.addEventListener('scroll', sync, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(sync).observe(track);
      sync();
    }

    disconnectedCallback() {
      document.removeEventListener('submit', this.onSubmit, true);
      document.removeEventListener('click', this.onBuyNow, true);
      if (this.section) this.section.removeEventListener('change', this.onFormChange);
    }

    form() {
      const forms = [...this.section.querySelectorAll('form[action*="/cart/add"]')];
      return (
        forms.find((f) => {
          const pid = f.querySelector('[name="product-id"]');
          return pid && Number(pid.value) === this.main.id;
        }) ||
        forms[0] ||
        null
      );
    }

    mainLine() {
      const form = this.form();
      const idInput = form && form.querySelector('[name="id"]');
      const id = idInput ? Number(idInput.value) : null;
      const qtyInput = form && form.querySelector('[name="quantity"]');
      const qty = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);
      const variant = this.main.variants.find((v) => v.id === id) || this.main.variants.find((v) => v.available) || this.main.variants[0];
      return { product: this.main, variant, qty };
    }

    selectedRecs() {
      return this.rows
        .filter((r) => r.check.checked)
        .map((r) => {
          const id = Number(r.select ? r.select.value : r.check.value);
          const variant = r.product.variants.find((v) => v.id === id) || r.product.variants.find((v) => v.available);
          return { product: r.product, variant, qty: 1 };
        })
        .filter((x) => x.variant && x.variant.available);
    }

    pieces() {
      const main = this.mainLine();
      return (main.variant && main.variant.available ? [main] : []).concat(this.selectedRecs());
    }

    hasExtras() {
      return this.selectedRecs().length > 0 || Boolean(this.pack && this.pack.selectedAll().length);
    }

    render() {
      this.rows.forEach((r) => {
        r.row.classList.toggle('is-on', r.check.checked);
        if (!r.select || !r.price) return;
        const variant = r.product.variants.find((v) => v.id === Number(r.select.value));
        if (!variant) return;
        r.price.textContent = formatMoney(variant.price);
        if (r.compare) r.compare.textContent = variant.compare > variant.price ? formatMoney(variant.compare) : '';
      });
      if (this.pack) this.pack.render();
      if (!this.saveEl) return;
      const pieces = this.pieces().filter((p) => inOffer(p.product));
      const count = pieces.reduce((n, p) => n + p.qty, 0);
      const info = tierInfo(count);
      const sub = pieces.reduce((n, p) => n + p.variant.price * p.qty, 0);
      if (info.current) {
        const save = Math.round((sub * info.current.pct) / 100);
        let html = `<b>${esc(S('youSave', { amount: formatMoney(save) }))}</b> · <s>${esc(formatMoney(sub))}</s>${esc(formatMoney(sub - save))}`;
        if (info.next) html += ` · ${esc(S('addMore', { count: info.next.qty - count, percent: info.next.pct }))}`;
        this.saveEl.innerHTML = html;
      } else if (info.next) {
        this.saveEl.textContent = S('addMore', { count: info.next.qty - count, percent: info.next.pct });
      } else {
        this.saveEl.textContent = '';
      }
    }

    onSubmit(event) {
      const form = this.form();
      if (!form || event.target !== form || !this.hasExtras()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.addAll(false, form);
    }

    onBuyNow(event) {
      const button = event.target.closest('.shopify-payment-button__button');
      if (!button || !this.section.contains(button) || !this.hasExtras()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.addAll(true, this.form());
    }

    async addAll(checkout, form) {
      if (this.busy) return;
      this.busy = true;
      if (this.errorEl) this.errorEl.hidden = true;
      const submit = form && form.querySelector('[type="submit"]');
      if (submit) submit.setAttribute('aria-busy', 'true');
      const boxId = newBoxId();
      const main = this.mainLine();
      const mainItems = main.variant && main.variant.available ? [{ id: main.variant.id, quantity: main.qty, properties: { _gift_box: boxId } }] : [];
      const items = mainItems.concat(
        this.selectedRecs().map((r) => ({ id: r.variant.id, quantity: 1, properties: { _gift_box: boxId } }))
      );
      if (this.pack) items.push(...this.pack.cartItems(boxId));
      try {
        await cartAdd(items);
        if (checkout) {
          window.location.href = `${(config().rootUrl || '/').replace(/\/?$/, '/')}checkout`;
          return;
        }
        this.rows.forEach((r) => {
          r.check.checked = false;
        });
        if (this.pack) this.pack.reset();
        this.render();
        const handled = await refreshCart(true);
        if (!handled) window.location.href = config().cartUrl || '/cart';
      } catch (error) {
        if (this.errorEl) {
          this.errorEl.textContent = error.message || S('error');
          this.errorEl.hidden = false;
        }
      } finally {
        this.busy = false;
        if (submit) submit.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('ss-gift-box', SSGiftBox);
  customElements.define('ss-gift-bundle', SSGiftBundle);
})();
